/**
 * Corps.js
 * PSP/CRE Tool v2.0 - Hierarchical Force Structure
 * 
 * Corps class - strategic formation of 2-4 divisions
 * Typically 40,000-100,000 personnel, has Role 3 and Role 4 medical facilities
 * 
 * Medical Hierarchy Position:
 * - Corps → Role 3 (Corps-level Combat Support Hospital)
 * - Corps → Role 4 (Strategic hospitals in rear areas)
 * - Receives casualties from division Role 3 facilities
 * - Provides definitive care and long-term treatment
 * 
 * Author: Dr. Tatar Radu MD
 * Institution: Romanian Military Medical Institute
 * Date: November 26, 2025
 * Version: 1.0
 */

import { BaseUnit } from './BaseUnit.js';

export class Corps extends BaseUnit {
  /**
   * Create a Corps
   * 
   * @param {Object} config - Corps configuration
   * @param {string} config.id - Unique identifier
   * @param {string} config.name - Corps name (e.g., "I Corps", "II Corps")
   * @param {number} [config.corps_number=1] - Corps number (I, II, III = 1, 2, 3)
   * @param {number} [config.strength=60000] - Personnel strength
   * @param {number} [config.position_feba_km=300] - Distance from FEBA (Romanian default: 300km)
   */
  constructor(config) {
    super({
      ...config,
      type: 'CORPS',
      strength: config.strength || 60000,
      position_feba_km: config.position_feba_km !== undefined ? config.position_feba_km : 300
    });
    
    // Corps-specific properties
    this.corps_number = config.corps_number || 1;   // I Corps = 1, II Corps = 2, etc.
    this.divisions = [];                            // Array of Division instances
    
    // Medical facilities
    this.role3_corps_facility = null;               // Corps-level Role 3 (if separate from divisions)
    this.role3_corps_facility_id = null;
    this.role4_facilities = [];                     // Array of Role 4 strategic hospitals
    this.role4_facility_ids = [];
    
    // Corps type
    this.corps_type = config.corps_type || 'FIELD'; // FIELD, HEAVY, AIRBORNE
  }
  
  // ============================================================================
  // DIVISION MANAGEMENT METHODS
  // ============================================================================
  
  /**
   * Add division to this corps
   * Establishes parent-child relationship
   * 
   * @param {Division} division - Division instance to add
   */
  addDivision(division) {
    this.addChild(division);
    this.divisions.push(division);
    division.parent_corps_id = this.id;
  }
  
  /**
   * Get all divisions in this corps
   * 
   * @returns {Division[]} Array of division instances
   */
  getDivisions() {
    return this.divisions;
  }
  
  /**
   * Get division by number
   * 
   * @param {number} division_number - Division number (1, 2, 3...)
   * @returns {Division|null} Division instance or null if not found
   */
  getDivisionByNumber(division_number) {
    return this.divisions.find(div => div.division_number === division_number) || null;
  }
  
  /**
   * Get all brigades across all divisions
   * Flattens the hierarchy
   * 
   * @returns {Brigade[]} Array of all brigade instances in corps
   */
  getAllBrigades() {
    return this.divisions.flatMap(div => div.getBrigades());
  }
  
  /**
   * Get all battalions across entire corps
   * Completely flattens the hierarchy
   * 
   * @returns {Battalion[]} Array of all battalion instances in corps
   */
  getAllBattalions() {
    return this.divisions.flatMap(div => div.getAllBattalions());
  }
  
  /**
   * Get total strength of all divisions
   * 
   * @returns {number} Sum of all division current strengths
   */
  getTotalDivisionStrength() {
    return this.divisions.reduce((sum, div) => sum + div.getAccurateStrength(), 0);
  }
  
  /**
   * Get corps strength as sum of division strengths plus corps HQ
   * 
   * @returns {number} Total corps strength
   */
  getAccurateStrength() {
    const division_total = this.getTotalDivisionStrength();
    const hq_strength = this.current_strength - this.divisions.reduce((sum, div) => sum + div.strength, 0);
    return division_total + Math.max(0, hq_strength);
  }
  
  // ============================================================================
  // FACILITY ID GENERATION
  // ============================================================================
  
  /**
   * Generate unique facility ID for corps Role 3
   * Format: role3_corps{X}
   * 
   * @returns {string} Facility ID
   */
  generateRole3CorpsFacilityId() {
    return `role3_corps${this.corps_number}`;
  }
  
  /**
   * Generate unique facility ID for Role 4 strategic hospital
   * Format: role4_corps{X}_{N}
   * 
   * @param {number} hospital_number - Sequential number (1, 2, 3...)
   * @returns {string} Facility ID
   */
  generateRole4FacilityId(hospital_number) {
    return `role4_corps${this.corps_number}_${hospital_number}`;
  }
  
  /**
   * Generate human-readable facility name for corps Role 3
   * 
   * @returns {string} Name like "I Corps Combat Support Hospital (Role 3)"
   */
  generateRole3FacilityName() {
    return `${this.name} Combat Support Hospital (Role 3)`;
  }
  
  // ============================================================================
  // MEDICAL INTEGRATION
  // ============================================================================
  
  /**
   * Link Corps-level Role 3 facility
   * 
   * @param {Role3Facility} facility - Medical facility object
   */
  linkRole3CorpsFacility(facility) {
    this.role3_corps_facility = facility;
    this.role3_corps_facility_id = facility.facility_id;
  }
  
  /**
   * Add Role 4 strategic hospital to corps
   * 
   * @param {Role4Facility} facility - Role 4 facility object
   */
  addRole4Facility(facility) {
    this.role4_facilities.push(facility);
    this.role4_facility_ids.push(facility.facility_id);
  }
  
  /**
   * Get all Role 3 facilities (divisions + corps)
   * 
   * @returns {Array} Array of Role 3 facility objects
   */
  getAllRole3Facilities() {
    const division_role3 = this.divisions
      .filter(div => div.role3_facility !== null)
      .map(div => div.role3_facility);
    
    if (this.role3_corps_facility) {
      return [this.role3_corps_facility, ...division_role3];
    }
    
    return division_role3;
  }
  
  /**
   * Get all Role 2 facilities across corps
   * 
   * @returns {Object} Object with role2_basic and role2_enhanced arrays
   */
  getAllRole2Facilities() {
    const role2_basic = this.divisions.flatMap(div => div.getAllBrigadeRole2Facilities());
    const role2_enhanced = this.divisions
      .filter(div => div.role2_enhanced_facility !== null)
      .map(div => div.role2_enhanced_facility);
    
    return {
      role2_basic: role2_basic,
      role2_enhanced: role2_enhanced,
      total: role2_basic.length + role2_enhanced.length
    };
  }
  
  /**
   * Get all Role 1 facilities across entire corps
   * 
   * @returns {Array} Array of all Role 1 facility objects
   */
  getAllRole1Facilities() {
    return this.divisions.flatMap(div => div.getAllBattalionRole1Facilities());
  }
  
  /**
   * Get complete medical facility inventory
   * 
   * @returns {Object} Complete facility breakdown by role
   */
  getMedicalFacilityInventory() {
    const role1 = this.getAllRole1Facilities();
    const role2 = this.getAllRole2Facilities();
    const role3 = this.getAllRole3Facilities();
    const role4 = this.role4_facilities;
    
    return {
      role1_count: role1.length,
      role2_basic_count: role2.role2_basic.length,
      role2_enhanced_count: role2.role2_enhanced.length,
      role3_count: role3.length,
      role4_count: role4.length,
      total_facilities: role1.length + role2.total + role3.length + role4.length,
      facilities: {
        role1: role1,
        role2_basic: role2.role2_basic,
        role2_enhanced: role2.role2_enhanced,
        role3: role3,
        role4: role4
      }
    };
  }
  
  // ============================================================================
  // AGGREGATION METHODS
  // ============================================================================
  
  /**
   * Get combat effectiveness for all divisions
   * 
   * @returns {Object} Summary of division effectiveness
   */
  getDivisionEffectivenessStatus() {
    const total = this.divisions.length;
    const effective = this.divisions.filter(div => div.isCombatEffective()).length;
    const degraded = this.divisions.filter(div => {
      const pct = div.getStrengthPercentage();
      return pct >= 50 && pct < 70;
    }).length;
    const ineffective = this.divisions.filter(div => {
      const pct = div.getStrengthPercentage();
      return pct > 0 && pct < 50;
    }).length;
    const destroyed = this.divisions.filter(div => div.current_strength === 0).length;
    
    return {
      total_divisions: total,
      combat_effective: effective,
      degraded: degraded,
      ineffective: ineffective,
      destroyed: destroyed,
      effectiveness_percentage: total > 0 ? (effective / total) * 100 : 0
    };
  }
  
  /**
   * Get effectiveness for all brigades across corps
   * 
   * @returns {Object} Summary
   */
  getAllBrigadeEffectivenessStatus() {
    const all_brigades = this.getAllBrigades();
    const total = all_brigades.length;
    const effective = all_brigades.filter(brig => brig.isCombatEffective()).length;
    const degraded = all_brigades.filter(brig => {
      const pct = brig.getStrengthPercentage();
      return pct >= 50 && pct < 70;
    }).length;
    const ineffective = all_brigades.filter(brig => {
      const pct = brig.getStrengthPercentage();
      return pct > 0 && pct < 50;
    }).length;
    const destroyed = all_brigades.filter(brig => brig.current_strength === 0).length;
    
    return {
      total_brigades: total,
      combat_effective: effective,
      degraded: degraded,
      ineffective: ineffective,
      destroyed: destroyed,
      effectiveness_percentage: total > 0 ? (effective / total) * 100 : 0
    };
  }
  
  /**
   * Get effectiveness for all battalions across corps
   * 
   * @returns {Object} Summary
   */
  getAllBattalionEffectivenessStatus() {
    const all_battalions = this.getAllBattalions();
    const total = all_battalions.length;
    const effective = all_battalions.filter(bn => bn.isCombatEffective()).length;
    const degraded = all_battalions.filter(bn => {
      const pct = bn.getStrengthPercentage();
      return pct >= 50 && pct < 70;
    }).length;
    const ineffective = all_battalions.filter(bn => {
      const pct = bn.getStrengthPercentage();
      return pct > 0 && pct < 50;
    }).length;
    const destroyed = all_battalions.filter(bn => bn.current_strength === 0).length;
    
    return {
      total_battalions: total,
      combat_effective: effective,
      degraded: degraded,
      ineffective: ineffective,
      destroyed: destroyed,
      effectiveness_percentage: total > 0 ? (effective / total) * 100 : 0
    };
  }
  
  /**
   * Calculate total casualties across entire corps
   * 
   * @returns {number} Total casualties
   */
  getTotalCasualties() {
    return this.divisions.reduce((sum, div) => sum + div.getTotalCasualties(), 0);
  }
  
  /**
   * Get organizational summary
   * 
   * @returns {Object} Unit counts at each echelon
   */
  getOrganizationalSummary() {
    return {
      divisions: this.divisions.length,
      brigades: this.getAllBrigades().length,
      battalions: this.getAllBattalions().length,
      total_units: this.divisions.length + this.getAllBrigades().length + this.getAllBattalions().length
    };
  }
  
  // ============================================================================
  // SERIALIZATION
  // ============================================================================
  
  /**
   * Convert to JSON for storage
   * 
   * @returns {Object} JSON representation
   */
  toJSON() {
    return {
      ...super.toJSON(),
      corps_number: this.corps_number,
      corps_type: this.corps_type,
      role3_corps_facility_id: this.role3_corps_facility_id,
      role4_facility_ids: this.role4_facility_ids,
      divisions_count: this.divisions.length,
      brigades_count: this.getAllBrigades().length,
      battalions_count: this.getAllBattalions().length,
      divisions: this.divisions.map(div => div.toJSON()),
      organizational_summary: this.getOrganizationalSummary(),
      division_effectiveness: this.getDivisionEffectivenessStatus(),
      brigade_effectiveness: this.getAllBrigadeEffectivenessStatus(),
      battalion_effectiveness: this.getAllBattalionEffectivenessStatus(),
      medical_facility_inventory: this.getMedicalFacilityInventory()
    };
  }
  
  /**
   * Get detailed summary
   * 
   * @returns {Object} Detailed summary
   */
  getDetailedSummary() {
    const base_summary = this.getSummary();
    
    return {
      ...base_summary,
      corps_number: this.corps_number,
      corps_type: this.corps_type,
      organizational_summary: this.getOrganizationalSummary(),
      total_division_strength: this.getTotalDivisionStrength(),
      division_effectiveness: this.getDivisionEffectivenessStatus(),
      brigade_effectiveness: this.getAllBrigadeEffectivenessStatus(),
      battalion_effectiveness: this.getAllBattalionEffectivenessStatus(),
      total_casualties: this.getTotalCasualties(),
      medical_facility_inventory: this.getMedicalFacilityInventory(),
      divisions_summary: this.divisions.map(div => ({
        id: div.id,
        name: div.name,
        division_number: div.division_number,
        brigades_count: div.brigades.length,
        battalions_count: div.getAllBattalions().length,
        strength: div.getAccurateStrength(),
        strength_percentage: div.getStrengthPercentage(),
        effectiveness: div.getCombatEffectivenessRating()
      }))
    };
  }
}

/**
 * USAGE EXAMPLES:
 * 
 * import { Corps } from './models/force/Corps.js';
 * import { Division } from './models/force/Division.js';
 * 
 * // Create a corps
 * const corps = new Corps({
 *   id: 'corps1',
 *   name: 'I Corps',
 *   corps_number: 1,
 *   corps_type: 'FIELD',
 *   strength: 60000,
 *   position_feba_km: 300
 * });
 * 
 * // Add divisions (with brigades and battalions)
 * for (let d = 1; d <= 3; d++) {
 *   const div = new Division({
 *     id: `div${d}`,
 *     name: `${d}th Division`,
 *     division_number: d,
 *     strength: 15000
 *   });
 *   
 *   // Add brigades to division (simplified)
 *   // ... (see Division examples)
 *   
 *   corps.addDivision(div);
 * }
 * 
 * // Get organizational summary
 * console.log(corps.getOrganizationalSummary());
 * // Output: { divisions: 3, brigades: 9, battalions: 27, total_units: 39 }
 * 
 * // Get all battalions
 * const all_bns = corps.getAllBattalions();
 * console.log(`Corps has ${all_bns.length} battalions total`);
 * 
 * // Get medical facility inventory
 * const facilities = corps.getMedicalFacilityInventory();
 * console.log(`Total medical facilities: ${facilities.total_facilities}`);
 * console.log(`Role 1: ${facilities.role1_count}`);
 * console.log(`Role 2: ${facilities.role2_basic_count + facilities.role2_enhanced_count}`);
 * console.log(`Role 3: ${facilities.role3_count}`);
 * console.log(`Role 4: ${facilities.role4_count}`);
 */
